import { inject, Injectable } from '@angular/core';
import { injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental';

import { Workflows } from 'src/providers/workflows/workflows';
import { Auth } from 'src/providers/auth/auth';

import { getEffectiveWorkflowTags } from 'src/shared/constants/workflowTags';
import { isHiddenAgentTrigger } from 'src/shared/constants/agentTriggers';

export const workflowKeys = {
  all: ['workflows'] as const,
  list: () => [...workflowKeys.all, 'list'] as const,
  triggerTypes: () => [...workflowKeys.all, 'trigger-types'] as const,
};

export interface AgentSummary {
  id: number;
  name: string;
  enabled: boolean;
  effectiveTags: string[];
  // The unified-agent trigger key this workflow lives under, in the same
  // format buildAllTriggers() uses (`workflow.<triggerId>` or the lowercased
  // trigger name when there is no id). Used for deep-linking into /agent.
  triggerKey: string | null;
}

@Injectable({ providedIn: 'root' })
export class WorkflowsQueriesService {
  private readonly workflowsProvider = inject(Workflows);
  private readonly auth = inject(Auth);
  private readonly queryClient = injectQueryClient();

  readonly workflowsQuery = injectQuery(() => ({
    queryKey: workflowKeys.list(),
    queryFn: () => this.workflowsProvider.getWorkflowsWithVersions(),
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: this.auth.authenticated(),
  }));

  readonly triggerTypesQuery = injectQuery(() => ({
    queryKey: workflowKeys.triggerTypes(),
    queryFn: () => this.workflowsProvider.getWorkflowTriggerTypes(),
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: this.auth.authenticated(),
  }));

  // All PUBLISHED workflows with their effective tags (explicit stored tags
  // plus the tag implied by each workflow's trigger category). Draft-only
  // workflows are excluded — they aren't operating agents, and test accounts
  // accumulate hundreds of abandoned drafts that would inflate the counts.
  async getAgentSummaries(): Promise<AgentSummary[]> {
    const [workflows, triggerTypes] = await Promise.all([
      this.getQueryData(this.workflowsQuery),
      this.getQueryData(this.triggerTypesQuery),
    ]);
    return (workflows || [])
      .filter((workflow) => !!workflow.published_version)
      // Agents on triggers the Agents page hides can't be opened there, so
      // don't advertise them — clicking one would have nowhere to go.
      .filter((workflow) => {
        const version = workflow.published_version || workflow.draft_version;
        return !isHiddenAgentTrigger(version?.workflow_version_trigger?.name);
      })
      .map((workflow) => {
        const version = workflow.published_version || workflow.draft_version;
        const versionTrigger = version?.workflow_version_trigger;
        const triggerKey = versionTrigger?.name
          ? `workflow.${versionTrigger.trigger_id || versionTrigger.name.trim().toLowerCase()}`
          : null;
        return {
          id: workflow.id,
          name: workflow.name,
          enabled: !workflow.paused_at,
          effectiveTags: getEffectiveWorkflowTags(workflow, triggerTypes || []),
          triggerKey,
        };
      });
  }

  async getAgentsForTag(tag: string): Promise<AgentSummary[]> {
    const summaries = await this.getAgentSummaries();
    return summaries.filter((summary) => summary.effectiveTags.includes(tag));
  }

  invalidate(): void {
    this.queryClient.invalidateQueries({ queryKey: workflowKeys.all });
  }

  private async getQueryData(query: { data: () => any; refetch: () => Promise<{ data?: any }> }): Promise<any[]> {
    const cached = query.data();
    if (cached) {
      return cached;
    }
    const result = await query.refetch();
    return result.data ?? [];
  }
}
